import { useToastError } from "@/components/ui/use-toast";
import { useApiRequest } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { companyRefusalKey, type CompanyRefusalKey } from "@/lib/company-refusal";
import { UIMessage, useChat } from "@ai-sdk/react";
import { ConversationMessageMetadata } from "@shaarp/common";
import { ChatStatus, DefaultChatTransport } from "ai";
import posthog from "posthog-js";
import { useEffect, useRef, useState } from "react";

/**
 * Module-level cache for completed AI stream results.
 * Keyed by streamUrl, stores the last assistant message so that
 * navigating away and back doesn't re-trigger the stream.
 */
const streamCache = new Map<
  string,
  UIMessage<ConversationMessageMetadata>
>();

/** Batches streamed chunks into one render per interval, as the chat does. */
export const STREAM_THROTTLE_MS = 50;

/** Check whether a stream URL already has cached results. */
export function hasStreamCache(streamUrl: string): boolean {
  return streamCache.has(streamUrl);
}

type UseAIStreamOptions = {
  streamUrl: string;
  launchFetch?: boolean;
  /**
   * The caller shows a refusal (daily budget, throttle, licence) where the
   * answer would be, from `refusal`: it is not toasted on top.
   */
  showsRefusal?: boolean;
  onStart?: () => void;
  onData?: (data: any) => void;
  onFinish?: () => void;
};

type UseAIStreamReturn = {
  assistantMessage: UIMessage<ConversationMessageMetadata> | null;
  status: ChatStatus;
  /** The last request was refused (daily budget, throttle, licence): its message. */
  refusal: CompanyRefusalKey | null;
  messages: UIMessage<ConversationMessageMetadata>[];
  /**
   * Asks for the answer. `regenerate`: the user asked for a new one — a route
   * that serves a stored answer while its data is fresh generates it anyway.
   */
  fetchData: (options?: { regenerate?: boolean }) => Promise<void>;
  stop: () => void;
  sendMessage: (message: { text: string }) => void;
  onStart: () => void;
  onData: (data: any) => void;
  onFinish: () => void;
};

/**
 * Custom hook to manage AI streaming with state and error handling
 * @param streamUrl - URL of the streaming endpoint
 * @param launchFetch - If true, automatically triggers the fetch on mount
 * @returns Object containing state and methods to manage AI streaming
 */
export const useAIMessageStream = ({
  streamUrl,
  launchFetch = true,
  showsRefusal = false,
  onStart = () => {},
  onData = () => {},
  onFinish = () => {},
}: UseAIStreamOptions): UseAIStreamReturn => {
  const { url, headers } = useApiRequest();
  const toastError = useToastError();
  const { t } = useTranslation();

  const [assistantMessage, setAssistantMessage] =
    useState<UIMessage<ConversationMessageMetadata> | null>(
      () => streamCache.get(streamUrl) ?? null,
    );

  const [refusal, setRefusal] = useState<CompanyRefusalKey | null>(null);

  // Read by `onError`: a card shows a refusal in place of an answer only, so a
  // refused regeneration under an answer already shown is toasted instead.
  const shownAnswerRef = useRef(assistantMessage);
  useEffect(() => {
    shownAnswerRef.current = assistantMessage;
  }, [assistantMessage]);

  const isMountedRef = useRef(true);

  const { messages, sendMessage, status, stop } = useChat<
    UIMessage<ConversationMessageMetadata>
  >({
    transport: new DefaultChatTransport({
      api: url(streamUrl),
      headers: () => headers(),
    }),
    // Reasoning models flush dozens of deltas within the same millisecond:
    // rendering once per chunk tripped React's nested-update limit, and the
    // card froze mid-sentence with no error.
    throttle: STREAM_THROTTLE_MS,
    onData: (data) => {
      onData?.(data);
    },
    onFinish: ({ message, isAbort, isDisconnect, isError, finishReason }) => {
      onFinish?.();
      if (!isMountedRef.current) return;

      // A connection reset — an instance killed mid-answer — lands here, not
      // below: the SDK only flags a disconnect together with an error.
      if (isError) {
        posthog.capture("ai_stream_cut", {
          stream_url: streamUrl,
          reason: isDisconnect ? "disconnect" : "error",
        });
        return;
      }

      // The throttled message store renders its last batch after the status
      // turns "ready": hand the final message over now, so a consumer that
      // reacts to "ready" (the financials forecast) never reads a stale text.
      setAssistantMessage(message);

      // A stop pressed by the user keeps what was written, as before.
      if (isAbort || finishReason === "stop") {
        streamCache.set(streamUrl, message);
        return;
      }

      // No finish chunk (the connection closed cleanly mid-answer, which the
      // SDK reports as a normal end), or the model stopped short (`length`,
      // `content-filter`...). Left uncached, so the next visit generates the
      // answer again instead of replaying the half of it.
      posthog.capture("ai_stream_cut", {
        stream_url: streamUrl,
        reason: finishReason ?? "no_finish_chunk",
      });
    },
    onError: (error) => {
      const refusalKey = companyRefusalKey(error);
      setRefusal(refusalKey);
      if (refusalKey && showsRefusal && !shownAnswerRef.current) return;
      toastError(refusalKey ? { description: t(refusalKey) } : undefined);
    },
  });

  // Auto-fetch only when there is no cached result
  useEffect(() => {
    if (launchFetch === true && !streamCache.has(streamUrl)) {
      fetchData();
    }
  }, [launchFetch]);

  useEffect(() => {
    if (status === "submitted") {
      onStart?.();
    }
  }, [status, onStart]);

  const stopRef = useRef(stop);
  stopRef.current = stop;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      stopRef.current();
    };
  }, []);

  // Update assistant message from streaming messages
  useEffect(() => {
    if (messages.length) {
      const assistantMessages = messages.filter((m) => m.role === "assistant");
      const lastAssistantMessage = assistantMessages.pop();

      setAssistantMessage(
        lastAssistantMessage as UIMessage<ConversationMessageMetadata>
      );
    }
  }, [messages]);

  /**
   * Triggers a new fetch (used by the regenerate button).
   * Always fetches fresh data, ignoring the cache.
   */
  const fetchData = async ({
    regenerate = false,
  }: { regenerate?: boolean } = {}): Promise<void> => {
    if (status === "streaming") {
      return;
    }

    setRefusal(null);
    sendMessage(
      {
        text: "",
      },
      regenerate ? { body: { regenerate: true } } : undefined,
    );
  };

  return {
    assistantMessage,
    status,
    refusal,
    messages,
    fetchData,
    stop,
    sendMessage,
    onStart,
    onData,
    onFinish,
  };
};
